import { useEffect, useState } from "react";

import { useGoogleMapsLoaded } from "@/hooks/useGoogleMapsLoaded";

// Roughly the national bounding box of Mexico (SW: Chiapas/Yucatan corner,
// NE: Baja/Coahuila corner). Used as a soft `bounds` bias on every
// Autocomplete instance so predictions are ranked Mexico-first without
// strictly filtering out the rest of the world. Replaces a previous
// `componentRestrictions: { country: ['mx'] }` hardcode — bias keeps the
// door open for future expansion without code changes, at the small risk
// of a non-MX result occasionally outranking an MX one for very ambiguous
// terms (e.g. "Madrid" might prefer Spain).
const MEXICO_BBOX_SW = { lat: 14.5, lng: -118.5 };
const MEXICO_BBOX_NE = { lat: 32.8, lng: -86.7 };

export const getMexicoBounds = () =>
    new window.google.maps.LatLngBounds(MEXICO_BBOX_SW, MEXICO_BBOX_NE);

// Convert a plain {south, west, north, east} bbox (the shape stored in the
// boundary JSONs under src/config/floors/) into a google.maps.LatLngBounds.
export const toGoogleBounds = (bbox) =>
    new window.google.maps.LatLngBounds(
        { lat: bbox.south, lng: bbox.west },
        { lat: bbox.north, lng: bbox.east }
    );

// Optional third argument: `{ biasBounds }` — a plain {south, west, north,
// east} bbox that replaces the Mexico-wide bias so a dedicated city page
// ranks local predictions first. Soft bias only, never a strict filter.
// Omitted (every pre-existing caller, homepage included) = Mexico bias,
// exactly as before.
export function useLeadingMaps(autocomplete, refLocationSearch, { biasBounds = null } = {}) {
    const [query, setQueryToSearch] = useState({});
    const isLoaded = useGoogleMapsLoaded();

    useEffect(() => {
        // Components now mount before the Maps script finishes loading (the
        // app-wide load gate is gone) — wait for it instead of crashing on
        // window.google being undefined.
        if (!isLoaded || !refLocationSearch.current) {
            return undefined;
        }
        const leadingMaps = async () => {
            autocomplete.current = new window.google.maps.places.Autocomplete(
                refLocationSearch.current,
                {
                    types: ["geocode", "establishment"],
                    bounds: biasBounds ? toGoogleBounds(biasBounds) : getMexicoBounds(),
                }
            );

            window.google.maps.event.addListener(
                autocomplete.current,
                "place_changed",
                function () {
                    const keysToFilled = {
                        location: "",
                        latitude: "",
                        longitude: "",
                        administrative_area_level_1: "state",
                        locality: "city",
                        sublocality_level_1: "neighborhood",
                    };
                    const componentForm = {
                        sublocality_level_1: "long_name", //Colonia
                        locality: "long_name", //Ciudad
                        administrative_area_level_1: "long_name", //Estado
                    };

                    // Llenamos el objeto para la query...
                    const place = autocomplete.current.getPlace();

                    if (!place || !place.geometry) {
                        // No asignes nada si no se selecciono un valor de google places
                        return;
                    }

                    keysToFilled.location = place.name;
                    keysToFilled.latitude = place.geometry.location.lat();
                    keysToFilled.longitude = place.geometry.location.lng();

                    for (var i = 0; i < place.address_components.length; i++) {
                        const addressType = place.address_components[i].types[0];
                        if (componentForm[addressType]) {
                            keysToFilled[`${addressType}`] =
                                place.address_components[i][componentForm[addressType]];
                        }
                    }

                    keysToFilled["location"] = refLocationSearch.current.value;
                    setQueryToSearch(keysToFilled);
                }
            );
        };
        leadingMaps();
        return () => {
            window.google?.maps?.event?.clearInstanceListeners(autocomplete);
            autocomplete.current = null;
        };
    }, [isLoaded, autocomplete, refLocationSearch, biasBounds]);

    return [query];
}

export function queryGenerator(
    query,
    typeValue = null,
    opValue = null,
    keyword = null,
    searchKeyword = null
) {
    const arrQuery = [];

    // 1. Añadir tipo y operación si existen
    if (typeValue) {
        arrQuery.push(`type=${Array.isArray(typeValue) ? typeValue.join(",") : typeValue}`);
    }
    if (opValue) {
        arrQuery.push(`operation=${Array.isArray(opValue) ? opValue.join(",") : opValue}`);
    }

    // 2. Manejar keywords y searchKeyword de forma excluyente
    if (keyword) {
        // Solo añadir keywords si no hay searchKeyword
        arrQuery.push(`keywords=${encodeURIComponent(keyword)}`);
    } else if (searchKeyword) {
        // Solo añadir searchKeyword si no hay keywords
        arrQuery.push(`searchKeyword=${encodeURIComponent(searchKeyword)}`);
    }

    // 3. Procesar datos de ubicación
    const parsersKeys = {
        administrative_area_level_1: "state",
        locality: "city",
        sublocality_level_1: "neighborhood",
    };

    const gMaps =
        query &&
        Object.keys(query).reduce((arr, key) => {
            const newFilterKey = parsersKeys[key];
            const value = query[key];

            // Solo añadir si el valor existe y no es igual a la key
            if (value && value !== newFilterKey) {
                const finalKey = newFilterKey || key;
                // No añadir si ya existe en los parámetros principales
                if (!["keywords", "searchKeyword"].includes(finalKey)) {
                    arr.push(`${finalKey}=${encodeURIComponent(value)}`);
                }
            }
            return arr;
        }, []);

    // Combinar todos los parámetros
    const finalQuery = arrQuery.concat(gMaps).join("&");
    return finalQuery;
}

export const TYPES = [
    { value: "", label: "Cualquiera" },
    { value: "HOUSE", label: "Casa" },
    { value: "TERRAIN", label: "Terreno/Lote" },
    { value: "APARTMENT", label: "Departamento" },
    { value: "WAREHOUSE", label: "Bodega" },
    { value: "BUILDING", label: "Edificio" },
    { value: "SHOP_SHOPPING_CENTER", label: "Local Comercial" },
    { value: "OFFICE", label: "Oficina" },
    { value: "SHOP", label: "Local" },
];
export const OPERATIONS = [
    { value: "PRESALE", label: "Preventa" },
    { value: "SALE", label: "Comprar" },
    { value: "LEASE", label: "Rentar" },
];

export const cardData = [
    //Arreglo para generar los iconos del carrusel de filtros
    {
        key: "HOUSE",
        label: "Casa",
        logo: "https://media.grupoguia.mx/assets/home-page/Homepage-v4/card/Group+48251.svg",
        types: ["HOUSE", "DUPLEX_HOUSE", "COTTAGE", "RANCH", "TH"],
    },
    {
        key: "APARTMENT",
        label: "Departamento",
        logo: "https://media.grupoguia.mx/assets/home-page/Homepage-v4/card/Group+48252.svg",
        types: ["APARTMENT", "LOFT", "STUDIO", "PENTHOUSE"],
    },
    {
        key: "TERRAIN",
        label: "Terreno/Lote",
        logo: "https://media.grupoguia.mx/assets/home-page/Homepage-v4/card/Group+48253.svg",
        types: ["TERRAIN"],
    },
    {
        key: "OFFICE",
        label: "Oficina",
        logo: "https://media.grupoguia.mx/assets/home-page/Homepage-v4/card/Group+48256.svg",
        types: ["OFFICE", "CONSULTING_ROOM"],
    },
    {
        key: "SHOP_SHOPPING_CENTER",
        label: "Local comercial",
        logo: "https://media.grupoguia.mx/assets/home-page/Homepage-v4/card/Group+48255.svg",
        types: ["SHOP", "SHOP_SHOPPING_CENTER", "EVENT_HALL"],
    },
    {
        key: "WAREHOUSE",
        label: "Bodega ",
        logo: "https://media.grupoguia.mx/assets/home-page/Homepage-v4/card/Group+48254.svg",
        types: ["WAREHOUSE", "INDUSTRIAL_BUILDING"],
    },
];

export const OPERATIONSARRAY = [
    { value: ["SALE"], label: "Comprar" },
    { value: ["LEASE"], label: "Rentar" },
    { value: ["PRESALE"], label: "Preventa" },
    { value: ["PRESALE", "LEASE", "SALE"], label: "Todas" },
];

export const orderOptions = [
    { value: "ASC", label: "Menor precio" },
    { value: "DESC", label: "Mayor precio" },
    { value: "NEARBY", label: "Más cercanos" },
];
